import React from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHandshake,
    faChartLine,
    faShieldAlt,
    faChalkboardTeacher,
    faUserFriends,
} from '@fortawesome/free-solid-svg-icons';
import { motion } from "framer-motion";
import { Breadcrumb } from "../components/Breadcrumb";
import ReadyToPlan from "../components/ReadyToPlan";

export default function SAGApproach() {
    return (
        <>

            <Breadcrumb currentName={"Our Approach"} currentParentName={"Our Approach"} />
            {/* Intro Section */}
            <section
                className="text-center py-5"
                style={{ backgroundColor: "var(--primary)", color: "var(--white)" }}
            >
                <motion.h1
                    className="fw-bold mb-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    A Human-Centered Approach to Financial Planning
                </motion.h1>
                <motion.p
                    className="mx-auto fs-5 w-75"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    At SAG Financials, we know that money isn’t just about math — it’s about life. Your life.
                </motion.p>
            </section>

            {/* Understanding Section */}
            <section className="container py-5">
                <motion.p
                    className="fs-5 text-center mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ color: "var(--text)" }}
                >
                    That’s why our approach goes beyond numbers or charts. We understand you first, then craft strategies aligned with your values and goals.
                </motion.p>
                <motion.p
                    className="fs-5 fst-italic text-center"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    style={{ color: "var(--secondary)" }}
                >
                    No templates. No assumptions. Just thoughtful, transparent planning.
                </motion.p>
            </section>

            {/* Core Philosophy */}
            <section className="py-5" style={{ backgroundColor: "var(--light-bg)" }}>
                <div className="container text-center">
                    <h2 className="fw-bold mb-5" style={{ color: "var(--heading)" }}>
                        Our Core Philosophy
                    </h2>
                    <div className="row g-4">
                        {[
                            { icon: faUserFriends, title: "Personal", text: "No two people are alike — so no two plans should be, either." },
                            { icon: faShieldAlt, title: "Transparent", text: "We believe in honesty — no hidden charges or jargon." },
                            { icon: faChartLine, title: "Goal-Driven", text: "We start with your life goals — not financial products." },
                            { icon: faHandshake, title: "Ongoing", text: "Your life changes, and your strategy evolves with it." },
                            { icon: faChalkboardTeacher, title: "Educative", text: "We explain every option clearly and simply." },
                        ].map(({ icon, title, text }, index) => (
                            <motion.div
                                key={title}
                                className="col-md-6 col-lg-4"
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <div
                                    className="card border-0 shadow h-100 p-4 rounded-3 d-flex flex-column align-items-center justify-content-center text-center"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-8px)";
                                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.1)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.05)";
                                    }}
                                >
                                    {/* Centered Icon */}
                                    <div
                                        className="d-flex align-items-center justify-content-center mb-3"
                                        style={{
                                            width: "70px",
                                            height: "70px",
                                            borderRadius: "50%",
                                            backgroundColor: "var(--light-bg)",
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={icon}
                                            style={{ color: "var(--secondary)", fontSize: "1.8rem" }}
                                        />
                                    </div>

                                    <h5 className="fw-semibold mb-2" style={{ color: "var(--heading)" }}>
                                        {title}
                                    </h5>
                                    <p className="text-muted">{text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>


            {/* 5-Step Process */}
            <section className="py-5">
                <div className="container">
                    <h2 className="text-center fw-bold mb-5" style={{ color: "var(--heading)" }}>
                        The SAG 5-Step Process
                    </h2>
                    <div className="row gy-4">
                        {[
                            { step: 1, title: "Discovery: Understanding You", content: "We start with an in-depth discussion to understand your goals and priorities." },
                            { step: 2, title: "Analysis & Assessment", content: "We analyze your income, savings, investments, and risks." },
                            { step: 3, title: "Tailored Strategy Development", content: "We create a personalized plan for investment, protection, and tax efficiency." },
                            { step: 4, title: "Implementation", content: "We help you set up investments and documentation transparently." },
                            { step: 5, title: "Monitoring & Adjustments", content: "We review and adapt your plan as your life evolves." },
                        ].map(({ step, title, content }, index) => (
                            <motion.div
                                key={step}
                                className="col-12"
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                            >
                                <div
                                    className="card shadow-sm p-4 border-0"
                                    style={{
                                        borderLeft: `5px solid var(--secondary)`,
                                        backgroundColor: "var(--white)",
                                    }}
                                >
                                    <h4 className="fw-bold mb-2" style={{ color: "var(--highlight)" }}>
                                        {step}. {title}
                                    </h4>
                                    <p style={{ color: "var(--text)" }}>{content}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
          <ReadyToPlan/>


        </>
    );
}
